import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
// @mui
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
// components
import { piiProps } from 'src/components/DemoBlur';
// redux
import { archiveFromCurrentPracticeAndMove } from '../../redux/slices/lead';
import { dispatch, useSelector } from '../../redux/store';
// utils
import { callApi } from '../../utils/apiCall';
import { buildIdentityFields, getIdentityCheck, hasCheckableContact } from '../../utils/leadIdentityApi';

// One duplicate check per destination selection, after a short pause so quickly flicking
// through the list does not fire a request (the endpoint is throttled) for every option.
export const DUPLICATE_CHECK_DEBOUNCE_MS = 250;

// Only the fields archiveFromCurrentPracticeAndMove copies to the new lead. The
// lightweight table field sets omit salutation / dob / is_registered_patient, so the
// lead is always re-read here rather than trusting whatever the caller had in hand.
const TRANSFER_LEAD_FIELDS = '{id,salutation,first_name,last_name,dob,is_registered_patient,lead_contact,lead_source,lead_address,is_archived,is_archive_pending,email_marketing_consent,sms_marketing_consent,whatsapp_marketing_consent,is_open_for_communication}';

export const TRANSFER_CONSEQUENCES = [
    "A new lead is created in the selected practice with the patient's personal details, contact details and lead source. It appears there as a NEW lead, and that practice's new-lead tasks and checklists are created.",
    "That practice's staff get their normal \"new lead\" in-app notifications.",
    'A note and a call reminder (due in 2 hours) are added to the new lead.',
    'The patient is not messaged automatically.',
    "Consent and communication preferences are copied from this lead.",
    "Treatments, appointments, tasks and history stay on this practice's (archived) lead.",
    "This practice's lead is then archived (reason: Another practice).",
];

/**
 * Shared confirmation dialog for moving a lead to another practice. Used by the
 * lead-detail menu and the lead-table row menu. `onDone(status, { archived })` runs
 * once the transfer has finished, whether or not every step succeeded; callers should
 * only navigate away / assume the lead is archived when `archived` is true.
 */
const TransferLeadDialog = ({ open, onClose, leadId, practiceId, leadName = '', onDone }) => {
    const practices = useSelector((state) => state.practiceTreatmentList?.practiceTreatment?.results);
    const [destination, setDestination] = useState(null);
    const [lead, setLead] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Duplicate check against the destination: { checking, match } - `match` is the (masked)
    // CRM match from the backend. A failed / timed-out / throttled check never blocks.
    const [duplicate, setDuplicate] = useState({ checking: false, match: null });

    const currentPractice = useMemo(
        () => (practices || []).find((practice) => String(practice?.id) === String(practiceId)),
        [practices, practiceId]
    );
    const destinations = useMemo(
        () => (practices || []).filter((practice) => String(practice?.id) !== String(practiceId)),
        [practices, practiceId]
    );

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setDestination(null);
        setLead(null);
        setLoadError('');
        setIsSubmitting(false);
        (async () => {
            try {
                const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/?fields=${TRANSFER_LEAD_FIELDS}`);
                if (cancelled) return;
                if (response?.status === 404) {
                    setLoadError('This lead could not be loaded \u2014 it may already be archived or you may no longer have access.');
                } else if (response?.status !== 200 || !response?.data?.id) {
                    setLoadError('Could not load the lead details. Please close this dialog and try again.');
                } else if (response.data.is_archived || response.data.is_archive_pending) {
                    setLoadError('This lead is already archived and cannot be transferred.');
                } else {
                    setLead(response.data);
                }
            } catch (error) {
                if (!cancelled) setLoadError('Could not load the lead details. Please close this dialog and try again.');
            }
        })();
        return () => { cancelled = true; };
    }, [open, leadId, practiceId]);

    useEffect(() => {
        setDuplicate({ checking: false, match: null });
        if (!destination || !lead) return undefined;
        const fields = buildIdentityFields({
            first_name: lead.first_name,
            last_name: lead.last_name,
            email: lead.lead_contact?.email,
            phone: lead.lead_contact?.phone,
            dob: lead.dob,
        });
        // The backend refuses to look anyone up without a phone / email: nothing to check.
        if (!hasCheckableContact(fields)) return undefined;
        let stale = false;
        setDuplicate({ checking: true, match: null });
        const timer = setTimeout(async () => {
            let result;
            try {
                result = await getIdentityCheck(destination.id, fields);
            } catch (error) {
                result = { ok: false };
            }
            if (stale) return;
            setDuplicate({ checking: false, match: result?.ok && result.check?.has_crm_match ? result.check.crm : null });
        }, DUPLICATE_CHECK_DEBOUNCE_MS);
        return () => { stale = true; clearTimeout(timer); };
    }, [destination, lead]);

    const duplicateLeads = duplicate.match ? [...duplicate.match.active, ...duplicate.match.archived] : [];
    const duplicateKinds = [duplicate.match?.active?.length ? 'active' : null, duplicate.match?.archived?.length ? 'archived' : null].filter(Boolean).join(' / ');
    const blockedByDuplicate = duplicateLeads.length > 0;

    const handleClose = () => {
        if (!isSubmitting) onClose();
    };

    const handleConfirm = () => {
        // The thunk is not idempotent (it creates a lead first), so never allow a second submit.
        if (isSubmitting || !lead || !destination || duplicate.checking || blockedByDuplicate) return;
        setIsSubmitting(true);
        dispatch(archiveFromCurrentPracticeAndMove(
            practiceId,
            destination.id,
            destination.name,
            currentPractice?.name,
            { originalData: lead },
            (status, result) => {
                setIsSubmitting(false);
                onDone?.(status, result);
            }
        ));
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm' sx={{ zIndex: 1500 }}>
            <DialogTitle>Transfer to another practice</DialogTitle>
            <DialogContent>
                <Typography variant='body2' sx={{ mb: 2 }}>
                    Move <span {...piiProps} style={{ fontWeight: 600 }}>{leadName || 'this lead'}</span>
                    {currentPractice?.name ? ` from ${currentPractice.name}` : ''} to another practice.
                </Typography>
                {loadError && <Alert severity='error' sx={{ mb: 2 }}>{loadError}</Alert>}
                <Autocomplete
                    options={destinations}
                    value={destination}
                    onChange={(event, value) => setDestination(value)}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    disabled={isSubmitting}
                    renderInput={(params) => <TextField {...params} label='Destination practice' placeholder='Select practice' />}
                />
                {duplicate.checking && (
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} color='inherit' /> Checking {destination?.name}...
                    </Typography>
                )}
                {blockedByDuplicate && (
                    <Alert severity='warning' sx={{ mt: 1.5 }}>
                        {destination?.name} already has a lead that matches this patient ({duplicateKinds}) {'\u2014'} transferring is not possible.
                        <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            {duplicateLeads.map((match, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={match.id ?? index}>
                                    <span {...piiProps}>{match.name || 'Lead'}</span>
                                    {match.status ? ` \u2013 ${String(match.status).replace(/_/g, ' ').toLowerCase()}` : ''}
                                </li>
                            ))}
                        </Box>
                    </Alert>
                )}
                <Typography variant='subtitle2' sx={{ mt: 2, mb: 0.5 }}>What will happen</Typography>
                <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                    {TRANSFER_CONSEQUENCES.map((text) => (
                        <Typography key={text} component='li' variant='body2' sx={{ mb: 0.5 }}>{text}</Typography>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button color='inherit' onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                <Button
                    variant='contained'
                    onClick={handleConfirm}
                    disabled={isSubmitting || !lead || !destination || duplicate.checking || blockedByDuplicate}
                    startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : null}
                >
                    {isSubmitting ? 'Transferring...' : 'Transfer & archive'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

TransferLeadDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadName: PropTypes.string,
    onDone: PropTypes.func,
};

export default TransferLeadDialog;
